<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Create Lesson Materials</h1>
      <div class="action-buttons">
        <button
          class="preview-button"
          @click="previewActivity"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'preview'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          Preview
        </button>
        <button
          class="draft-button"
          @click="saveActivity('draft')"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'draft'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          {{
            isSubmitting && savingType === "draft"
              ? "Saving..."
              : "Save as Draft"
          }}
        </button>
        <button
          class="publish-button"
          @click="saveActivity('published')"
          :disabled="isSubmitting"
        >
          <div
            v-if="isSubmitting && savingType === 'published'"
            class="spinner"
          ></div>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
            ></path>
            <polyline points="17 21 17 13 7 13 7 21"></polyline>
            <polyline points="7 3 7 8 15 8"></polyline>
          </svg>
          {{
            isSubmitting && savingType === "published"
              ? "Publishing..."
              : "Save as Published"
          }}
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="showValidationErrors" class="validation-errors">
      <div class="error-header">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <h3>Please fix the following errors:</h3>
        <button class="close-error" @click="showValidationErrors = false">
          ×
        </button>
      </div>
      <ul>
        <li v-for="(error, field) in validationErrors" :key="field">
          {{ error }}
        </li>
      </ul>
    </div>

    <div class="admin-content-layout">
      <div class="admin-main-content">
        <TitleSection
          ref="titleSectionRef"
          @update:title="updateTitle"
          @update:subtitle="updateSubtitle"
          @update:duration="updateDuration"
        ></TitleSection>
        <LessonMaterialsEditor
          ref="editorRef"
          @tabs-updated="updateTabs"
        ></LessonMaterialsEditor>
      </div>
      <div
        class="admin-right-sidebar"
        :class="{ 'mobile-open': rightSidebarOpen }"
      >
        <div class="admin-sidebar-header">
          <button
            class="close-sidebar-button"
            @click="toggleRightSidebar"
            v-if="isMobile"
          >
            Close Sidebar
          </button>
        </div>
        <div class="admin-sidebar-content">
          <!-- Sidebar content here -->
          <RightSidebar
            ref="rightSidebarRef"
            @cover-image-updated="updateCoverImage"
            @tag-sections-updated="updateTagSections"
            @category-sections-updated="updateCategorySections"
            @permalink-updated="updatePermalink"
            @grade-updated="updateGrade"
            @focus-updated="updateFocus"
            @airtable-id-updated="updateAirtableId"
            @subject-updated="updateSubject"
            @school-updated="updateSchool"
            @visibility-updated="updateVisibility"
            @locale-updated="updateLocale"
          ></RightSidebar>
        </div>
      </div>
    </div>

    <!-- Snackbar Notification -->
    <div v-if="notification.show" class="snackbar" :class="notification.type">
      <div class="snackbar-content">
        <svg
          v-if="notification.type === 'success'"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ notification.message }}</span>
      </div>
      <button class="snackbar-close" @click="closeNotification">×</button>
    </div>

    <button
      class="floating-button"
      @click="toggleRightSidebar"
      v-if="isMobile && !rightSidebarOpen"
    >
      Open Sidebar
    </button>
    <div
      class="right-sidebar-overlay"
      v-if="rightSidebarOpen && isMobile"
      @click="toggleRightSidebar"
    ></div>
  </div>
</template>

<script setup>
/* eslint-disable */
import TitleSection from "@/components/admin/lesson-plans/TitleSection.vue";
import LessonMaterialsEditor from "@/components/admin/lesson-materials/LessonMaterialsEditor.vue";
import RightSidebar from "@/components/admin/lesson-materials/RightSidebar.vue";
import makercartActivityService from "@/services/makercartActivityService";
import { useUnsavedChanges } from "@/composeables/useUnsavedChanges";
import { inject, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const rightSidebarOpen = ref(false);
const isMobile = ref(window.innerWidth < 991);
const editorRef = ref(null);
const rightSidebarRef = ref(null);
const titleSectionRef = ref(null);
const isSubmitting = ref(false);
const savingType = ref(null); // Tracks which save operation is in progress
const validationErrors = ref({});
const showValidationErrors = ref(false);

const notify = inject("notify");

// Notification system
const notification = ref({
  show: false,
  message: "",
  type: "success", // success or error
  timeout: null,
});

const closeNotification = () => {
  notification.value.show = false;
  if (notification.value.timeout) {
    clearTimeout(notification.value.timeout);
  }
};

// Activity data
const activityData = ref({
  title: "",
  subtitle: "",
  duration: "",
  coverImage: null,
  permalink: "",
  tagSections: [],
  categorySections: [],
  tabs: [],
  materials: [],
  scormUuid: null,
  scormId: null,
  scormFile: null, // For uploaded SCORM files
  scormMethod: "select", // 'select' or 'upload'
  grade: "",
  focus: "",
  airtable_id: "",
  subject: "",
  school: [],
  school_names: [],
  visibility: "",
  locale: [],
});

// Initialize unsaved changes tracking
const { markAsChanged, markAsSaved, setInitialLoading } = useUnsavedChanges(
  activityData,
  {
    confirmMessage:
      "You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.",
  }
);

// Update methods for two-way binding
const updateTitle = (title) => {
  activityData.value.title = title;
  markAsChanged();
};

const updateSubtitle = (subtitle) => {
  activityData.value.subtitle = subtitle;
  markAsChanged();
};

const updateDuration = (duration) => {
  activityData.value.duration = duration;
  markAsChanged();
};

const updateCoverImage = (coverImage) => {
  activityData.value.coverImage = coverImage;
  markAsChanged();
};

const updatePermalink = (permalink) => {
  activityData.value.permalink = permalink;
  markAsChanged();
};

const updateTagSections = (tagSections) => {
  activityData.value.tagSections = tagSections;
  markAsChanged();
};

const updateCategorySections = (categorySections) => {
  activityData.value.categorySections = categorySections;
  markAsChanged();
};

const updateTabs = (tabs, materials) => {
  activityData.value.tabs = tabs;

  // Update instructional materials if provided
  if (materials && Array.isArray(materials)) {
    activityData.value.materials = materials;
  }
  markAsChanged();
};

const updateGrade = (grade) => {
  activityData.value.grade = grade;
  markAsChanged();
};

const updateFocus = (focus) => {
  activityData.value.focus = focus;
  markAsChanged();
};

const updateAirtableId = (airtableId) => {
  activityData.value.airtable_id = airtableId;
  markAsChanged();
};

const updateSubject = (subject) => {
  activityData.value.subject = subject;
  markAsChanged();
};

const updateSchool = (school) => {
  activityData.value.school = school;
  markAsChanged();
};

const updateVisibility = (visibility) => {
  activityData.value.visibility = visibility;
  markAsChanged();
};

const updateLocale = (loc) => {
  activityData.value.locale = loc || [];
  markAsChanged();
};

const handleResize = () => {
  isMobile.value = window.innerWidth < 991;
};

// Load data from localStorage when the component mounts
const loadFromLocalStorage = () => {
  try {
    setInitialLoading(true);
    const savedData = localStorage.getItem("makercartActivityPreviewData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);

      // Validate that we have the expected structure before using the data
      if (parsedData) {
        let parsedLocale = [];
        if (parsedData.locale) {
          if (Array.isArray(parsedData.locale)) {
            parsedLocale = parsedData.locale;
          } else if (typeof parsedData.locale === "string") {
            parsedLocale = parsedData.locale.split(",").map((l) => l.trim());
          }
        }

        // Update the activity data
        activityData.value = {
          ...activityData.value,
          ...parsedData,
          grade: parsedData.grade || "",
          focus: parsedData.focus || "",
          airtable_id: parsedData.airtable_id || "",
          subject: parsedData.subject || "",
          school: parsedData.school || [],
          visibility: parsedData.visibility || "",
          locale: parsedLocale,
        };

        // Notify child components to update their state
        // This will happen in the next tick after the components are mounted
        if (titleSectionRef.value) {
          titleSectionRef.value.updateFromParent(
            parsedData.title || "",
            parsedData.subtitle || "",
            parsedData.duration || ""
          );
        }

        if (editorRef.value) {
          editorRef.value.updateFromParent(
            parsedData.tabs || [],
            parsedData.materials || []
          );
        }

        if (rightSidebarRef.value) {
          // Prepare SCORM data based on method
          let scormData = null;
          if (parsedData.scormMethod === "upload" && parsedData.scormFile) {
            scormData = {
              method: "upload",
              uploadedFile: parsedData.scormFile,
            };
          } else if (
            parsedData.scormMethod === "select" &&
            parsedData.scormUuid &&
            parsedData.scormId
          ) {
            scormData = {
              uuid: parsedData.scormUuid,
              id: parsedData.scormId,
            };
          }

          rightSidebarRef.value.updateFromParent(
            parsedData.coverImage || null,
            parsedData.tagSections || [],
            parsedData.permalink || "",
            scormData,
            {
              // Pass restored data as editFormData to hydrate sidebar fields including schools
              school_objects: parsedData.school || [],
              grade: parsedData.grade,
              focus: parsedData.focus,
              airtable_id: parsedData.airtable_id,
              subject: parsedData.subject,
              maker_approach: parsedData.maker_approach,
              material_provided: parsedData.material_provided,
              public_visibility: parsedData.visibility === "public_preview",
              locale: parsedLocale,
            },
            parsedData.categorySections || [],
            parsedLocale
          );
        }

        // Initialize saved state after loading from localStorage
        // Use setTimeout to ensure all child component updates are complete
        setTimeout(() => {
          markAsSaved();
          setInitialLoading(false);
        }, 100);
      }
    }
  } catch (error) {
    console.error("Error loading activity data from localStorage:", error);
    setInitialLoading(false);
  }
};

onMounted(() => {
  window.addEventListener("resize", handleResize);

  // Load data from localStorage
  setTimeout(() => {
    loadFromLocalStorage();
  }, 100); // Small delay to ensure components are mounted
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

function toggleRightSidebar() {
  rightSidebarOpen.value = !rightSidebarOpen.value;
}

function previewActivity() {
  // Check if activity has been saved
  if (!activityData.value.id) {
    // Show notification that activity needs to be saved first
    notify.warning(
      "Please save your activity as a draft or published before previewing.",
      "Save Required"
    );
    return;
  }

  // Navigate to preview page with activity ID
  router.push({
    name: "LessonPlansPreviewPage",
    params: { id: activityData.value.id },
  });
}

// Generate a slug from title
const generateSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^\w\s]/gi, "")
    .replace(/\s+/g, "-");
};

// Format categories for API
const formatCategories = (tagSections) => {
  if (!tagSections || !tagSections.length) return [];

  return tagSections
    .filter(
      (section) =>
        section.id !== "standards" &&
        section.selected &&
        section.selected.length
    )
    .map((section) => {
      const termIds = section.selected
        .map((tag) => {
          try {
            const parsed = typeof tag === "string" ? JSON.parse(tag) : tag;
            return parsed.id;
          } catch (e) {
            return null;
          }
        })
        .filter((id) => id !== null);

      return {
        category_id: section.id,
        term_id: termIds,
      };
    });
};

// Validate activity data
const validateActivityData = () => {
  const errors = {};

  if (!activityData.value.title.trim()) {
    errors.title = "Title is required";
  }

  if (!activityData.value.duration) {
    errors.duration = "Duration is required";
  }

  if (!activityData.value.coverImage) {
    errors.coverImage = "Cover image is required";
  }

  // Add validation for specific required tag sections
  if (!activityData.value.grade) {
    errors.grade = "Grade is required";
  }

  if (!activityData.value.focus) {
    errors.focus = "Focus is required";
  }

  if (!activityData.value.airtable_id) {
    errors.airtable_id = "Lesson Materials is required";
  }

  if (
    !activityData.value.locale ||
    (Array.isArray(activityData.value.locale) &&
      activityData.value.locale.length === 0)
  ) {
    errors.locale = "Locale is required";
  }

  validationErrors.value = errors;

  // Show errors UI if there are errors
  if (Object.keys(errors).length > 0) {
    showValidationErrors.value = true;
  }

  return Object.keys(errors).length === 0;
};

// Save activity
const saveActivity = async (status) => {
  if (isSubmitting.value) return;

  // Validate data
  if (!validateActivityData()) {
    return;
  }

  try {
    isSubmitting.value = true;
    savingType.value = status;
    validationErrors.value = {};
    showValidationErrors.value = false;

    // Prepare data for API
    const formData = new FormData();
    formData.append("title", activityData.value.title);
    formData.append("slug", generateSlug(activityData.value.title));
    formData.append("content", JSON.stringify(activityData.value.tabs));
    formData.append("short_description", activityData.value.subtitle);
    formData.append("duration", activityData.value.duration);
    formData.append("status", status);
    formData.append("permalink", activityData.value.permalink);
    formData.append("grade", activityData.value.grade);
    formData.append("focus", activityData.value.focus);
    formData.append("airtable_id", activityData.value.airtable_id);
    formData.append("subject", activityData.value.subject);

    let localePayload = "";
    if (
      Array.isArray(activityData.value.locale) &&
      activityData.value.locale.length > 0
    ) {
      localePayload = activityData.value.locale.join(", ");
    } else if (
      typeof activityData.value.locale === "string" &&
      activityData.value.locale.length > 0
    ) {
      localePayload = activityData.value.locale;
    }
    formData.append("locale", localePayload);

    // Add school_objects (JSON string of array of objects)
    if (activityData.value.school && activityData.value.school.length > 0) {
      formData.append(
        "school_objects",
        JSON.stringify(
          activityData.value.school.map((s) => ({
            title: s.title || s.name,
            country: s.country || "",
          }))
        )
      );
    } else {
      formData.append("school_objects", JSON.stringify([]));
    }

    // Add public_visibility if visibility is "public_preview"
    if (activityData.value.visibility === "public_preview") {
      formData.append("public_visibility", "true");
    }

    let slideData = null;

    // Add slide URL if slides are attached
    if (editorRef.value && editorRef.value.isSlideAttached()) {
      slideData = editorRef.value.getSlideData();
      if (slideData) {
        formData.append("slide_url", slideData.url);
      }
    }

    formData.append(
      "date",
      new Date()
        .toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "-")
    );

    // Process categories from both tagSections and categorySections
    const allCategories = [
      ...formatCategories(activityData.value.tagSections),
      ...formatCategories(activityData.value.categorySections),
    ];
    if (allCategories.length > 0) {
      formData.append("categories", JSON.stringify(allCategories));
    }

    // Process cover image
    if (activityData.value.coverImage) {
      // If the image is a base64 string, convert it to a file
      if (activityData.value.coverImage.startsWith("data:image")) {
        const imageBlob = await fetch(activityData.value.coverImage).then((r) =>
          r.blob()
        );
        const imageFile = new File(
          [imageBlob],
          `cover-image-${Date.now()}.jpg`,
          { type: "image/jpeg" }
        );
        formData.append("thumbnail", imageFile);
      } else {
        // If it's already a file, just append it
        formData.append("thumbnail", activityData.value.coverImage);
      }
    }

    // Call API
    const response =
      await makercartActivityService.createEducatorGuide(formData);
    const newActivity = response?.data?.data;

    // Store the activity ID for preview functionality
    if (newActivity && newActivity.id) {
      activityData.value.id = newActivity.id;
    }

    if (newActivity && activityData.value.materials?.length > 0) {
      // filter undefined materials
      const materials = activityData.value.materials
        .filter((material) => material !== undefined)
        .map((material, index) => ({
          educator_guide_id: newActivity.id,
          name: material.title,
          url: material.url,
          order: index,
        }));

      if (materials.length > 0) {
        await makercartActivityService.createBulkMaterials(materials);
      }
    }

    if (newActivity && slideData) {
      await makercartActivityService.assignSlides({
        educator_guide_id: newActivity.id,
        code: slideData.code,
        replace_existing: false,
      });
    }

    const successMessage = `Lesson materials ${
      status === "published" ? "published" : "saved as draft"
    } successfully!`;

    notify.success(successMessage, "Success");

    // Mark as saved after successful save
    markAsSaved();

    router.push({ name: "LessonMaterialsListPage" });
  } catch (error) {
    console.error("Error saving lesson materials:", error.response);
    const errorMessage =
      error?.response?.data?.message ||
      "Failed to save lesson materials. Please try again.";
    notify.error(errorMessage, "Error");
    showValidationErrors.value = true;
    validationErrors.value = {
      api: errorMessage,
    };
  } finally {
    isSubmitting.value = false;
    savingType.value = null;
  }
};
</script>

<style scoped>
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.preview-button,
.draft-button,
.publish-button,
.clear-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.preview-button {
  background-color: var(--primary-blue);
  color: white;
}

.preview-button:hover {
  opacity: 0.8;
}

.draft-button {
  background-color: #6c757d;
  color: white;
}

.draft-button:hover {
  background-color: #5a6268;
}

.publish-button {
  background-color: #28a745;
  color: white;
}

.publish-button:hover {
  background-color: #218838;
}

.clear-button {
  background-color: #e74c3c;
  color: white;
}

.clear-button:hover {
  background-color: #c0392b;
}

/* Button disabled state */
button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Spinner for loading state */
.action-buttons .spinner {
  width: 16px !important;
  height: 16px !important;
  border: 2px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 50% !important;
  border-top-color: #fff !important;
  animation: spin 0.8s linear infinite !important;
  background-color: transparent !important;
  box-shadow: none !important;
  margin: 0 !important;
  display: inline-block !important;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Validation errors */
.validation-errors {
  margin-bottom: 20px;
  border: 1px solid #f8d7da;
  border-radius: 4px;
  background-color: #fff5f6;
  overflow: hidden;
}

.error-header {
  display: flex;
  align-items: center;
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px 15px;
  font-weight: bold;
  gap: 10px;
}

.error-header h3 {
  margin: 0;
  flex-grow: 1;
}

.close-error {
  background: none;
  border: none;
  color: #721c24;
  font-size: var(--fs-big);
  cursor: pointer;
}

.validation-errors ul {
  margin: 0;
  padding: 15px 15px 15px 40px;
  color: #721c24;
}

.validation-errors li {
  margin-bottom: 5px;
}

/* Snackbar Styles */
.snackbar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  min-width: 300px;
  max-width: 80%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-radius: 4px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: slide-up 0.3s ease-out;
}

.snackbar.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.snackbar.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.snackbar-close {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: inherit;
  padding: 0;
  margin-left: 15px;
}

@keyframes slide-up {
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .admin-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .action-buttons {
    margin-top: 10px;
    width: 100%;
    flex-direction: column;
  }

  .attachment-buttons {
    width: 100%;
    margin-bottom: 10px;
  }

  .preview-button,
  .draft-button,
  .publish-button,
  .attachment-button {
    width: 100%;
    justify-content: center;
  }

  .snackbar {
    width: 90%;
    bottom: 10px;
  }
}
</style>
